import { haggleApiService } from './haggle.service';

import { SubscriptionPlans } from '@/components/utils/enums';
import { InventoryType } from '@/interfaces/api/InventoryType';

interface SubscriptionServiceOptions {
	email?: string;
	plan?: string;
	storeName?: string;
}

class SubscriptionService {
	async fetchUserPlanDetails(user: any, store: any): Promise<any> {
		try {
			if (user && user.inventoryType === InventoryType.CSV) {
				return await this.fetchStripeUserPlanDetails(user.username);
			} else if (store && store.shop) {
				return await this.fetchShopifyUserPlanDetails(store.tradeName);
			}

			return {
				planName: SubscriptionPlans.emerald,
				planPrice: 0,
				endsOn: 'N/A',
				cancelled: false,
				nextPlan: null,
			};
		} catch (error: Error | any) {
			return {
				planName: SubscriptionPlans.emerald,
				planPrice: 0,
				endsOn: 'N/A',
				cancelled: false,
				nextPlan: null,
			};
		}
	}

	private async fetchStripeUserPlanDetails(username: string): Promise<any> {
		try {
			const response = await haggleApiService.getUserPlan(username);
			if (response && response.planName) {
				return response;
			}
		} catch (error: Error | any) {
			if (error.message !== 'Error: No active subscription found') {
				throw error;
			}
		}

		return {
			planName: SubscriptionPlans.emerald,
			planPrice: 0,
			endsOn: 'N/A',
			cancelled: false,
			nextPlan: null,
		};
	}

	private async fetchShopifyUserPlanDetails(tradeName: string): Promise<any> {
		try {
			const response = await haggleApiService.getUserPlanShopify(tradeName);
			if (response) {
				let planData = {
					planName: SubscriptionPlans.emerald,
					planPrice: 0,
					endsOn: 'N/A',
					cancelled: false,
					nextPlan: null,
				};

				for (let i = 0; i < response.length; i++) {
					if (response[i].node.status.toLowerCase() === 'active') {
						planData = {
							...planData,
							planName: response[i].node.name.toUpperCase(),
							planPrice:
								response[i].node.lineItems[0].plan.pricingDetails.price.amount,
							endsOn: response[i].node.currentPeriodEnd,
							nextPlan: response[i].node.name.toUpperCase(),
						};
						break;
					} else if (response[i].node.status.toLowerCase() === 'pending') {
						planData = {
							...planData,
							cancelled: true,
							nextPlan: response[i].node.name.toUpperCase(),
						};
					}
				}

				return planData;
			}
		} catch (error: Error | any) {
			if (error.message !== 'Error: No active subscription found') {
				throw error;
			}
		}

		return {
			planName: SubscriptionPlans.emerald,
			planPrice: 0,
			endsOn: 'N/A',
			cancelled: false,
			nextPlan: null,
		};
	}

	async updateRole(user: any, store: any, planData: any): Promise<any> {
		if (user && user.inventoryType === InventoryType.CSV) {
			return await this.updateRoleStripe({
				email: user.username,
				plan: planData.planName,
			});
		} else if (user && store && store.shop) {
			return await this.updateRoleShopify({
				email: user.username,
				plan: planData.planName,
				storeName: store.tradeName,
			});
		}

		throw new Error('Unable to update role: missing user or store data');
	}

	private async updateRoleStripe(
		options: SubscriptionServiceOptions,
	): Promise<any> {
		return await haggleApiService.updateRole(options);
	}

	private async updateRoleShopify(
		options: SubscriptionServiceOptions,
	): Promise<any> {
		return await haggleApiService.updateRoleShopify(options);
	}

	async manageSubscription(user: any, store: any, plan: string): Promise<any> {
		if (user && user.inventoryType === InventoryType.CSV) {
			return await haggleApiService.manageSubscription({
				email: user.username,
				plan,
			});
		} else if (store && store.shop) {
			return await haggleApiService.manageSubscriptionShopify({
				planName: plan,
				storeName: store.tradeName,
			});
		}

		throw new Error(
			'Unable to manage subscription: missing user or store data',
		);
	}

	async accessBillingPortal(user: any, store: any): Promise<{ url: string }> {
		if (user && user.inventoryType === InventoryType.CSV) {
			return await haggleApiService.updateCardDetails({
				email: user.username,
			});
		} else if (store && store.shop) {
			return {
				url: `https://admin.shopify.com/store/${store.tradeName}/settings/billing`,
			};
		}

		throw new Error(
			'Unable to access billing portal: missing user or store data',
		);
	}
}

export const subscriptionService = new SubscriptionService();
